/**
 * TrialCountdownBanner — Stage 11.
 *
 * Calm, premium trial countdown banner shown above the Home greeting once
 * the user has ≤7 days of trial left (plus a dedicated "Expired grace" mode).
 *
 * Visual states:
 *   • 7-4 days left  → warm teal tone, relaxed copy
 *   • 3 days left    → gentle amber, "a few days left"
 *   • 1 day left     → stronger amber, clear "tomorrow"
 *   • Expired (grace) → calm navy paywall framing, no bright red (audit rule:
 *                       "warmth not hard-lock")
 *
 * Every state surfaces the user's ACCUMULATED VALUE — documents, chats,
 * one-pagers, doctor summaries, days tracked — so "what you lose if you
 * cancel" is visible next to the countdown. This was the audit's highest-
 * leverage paid-conversion lever.
 *
 * Auto-hides when:
 *   • User is on active_paid (nothing to count down).
 *   • Trial has >7 days left (nothing urgent yet).
 *
 * Tapping CTA → /pricing to start the Stripe checkout flow (which you
 * already have live — this component never touches Stripe itself).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Sparkle, ArrowRight, ShieldCheck } from '@phosphor-icons/react';

/** Friendly tense-aware countdown copy. */
const copyFor = (days) => {
  if (days == null)    return null;
  if (days >= 8)       return null;           // not urgent yet
  if (days === 7)      return { eyebrow: "What you've built", tone: 'teal',  headline: "You've built real history in Kindra.", sub: 'A week of trial left — keep your record growing.' };
  if (days >= 4)       return { eyebrow: 'Your trial', tone: 'teal',  headline: `${days} days left in your trial.`, sub: 'All your memory stays yours either way.' };
  if (days === 3)      return { eyebrow: '3 days left', tone: 'amber', headline: "3 days left — here's what you've built.", sub: 'Continuing keeps every report, summary, and conversation active.' };
  if (days === 2)      return { eyebrow: 'Your trial is wrapping up', tone: 'amber', headline: '2 days left in your trial.', sub: "Keep everything you've built." };
  if (days === 1)      return { eyebrow: 'Tomorrow', tone: 'amber', headline: 'Tomorrow your trial ends.', sub: 'Keep your accumulated health intelligence active for $9.99/mo.' };
  return                      { eyebrow: 'Trial ended', tone: 'navy',  headline: 'Your trial just wrapped.', sub: "Your record is safe. Continue when you're ready." };
};

/** Stage 12 — grace-mode dedicated copy (days 0-3 after trial_end). */
const graceCopy = (graceDaysLeft) => {
  // Day 30: 2-3 days of grace remain → calm framing.
  // Day 32-33: 0-1 day → final reminder.
  if (graceDaysLeft >= 2)  return { eyebrow: 'Grace period · 3 days', tone: 'navy',
    headline: 'Your trial wrapped — your record stays read-only for 3 days.',
    sub: 'Open Kindra, view your timeline, export anything. New chats and uploads pause until you continue.' };
  if (graceDaysLeft === 1) return { eyebrow: 'Grace period · 1 day left', tone: 'amber',
    headline: 'Tomorrow your record locks unless you continue.',
    sub: 'Continue with Kindra Premium or export your record from Profile.' };
  return { eyebrow: 'Final day of grace', tone: 'amber',
    headline: 'Last day of read-only access.',
    sub: 'After tonight, your record locks unless you continue. Nothing is ever deleted.' };
};

const TONE_STYLES = {
  teal:  { border: 'border-[#2FB3A0]/30', bg: 'bg-gradient-to-br from-[#2FB3A0]/[0.06] to-white', iconBg: 'bg-[#2FB3A0]', iconText: 'text-white', eyebrow: 'text-[#2FB3A0]' },
  amber: { border: 'border-amber-200',   bg: 'bg-gradient-to-br from-amber-50 to-white',          iconBg: 'bg-amber-500',  iconText: 'text-white', eyebrow: 'text-amber-700' },
  navy:  { border: 'border-[#0E1C2E]/15', bg: 'bg-[#0E1C2E]',                                     iconBg: 'bg-[#2FB3A0]',  iconText: 'text-white', eyebrow: 'text-white/60', invert: true },
};

const ValueBullet = ({ n, label, invert }) => {
  if (!n) return null;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap ${invert ? 'text-white/80' : 'text-slate-600'}`}>
      <span className={`font-serif text-[15px] tabular-nums ${invert ? 'text-white' : 'text-[#0E1C2E]'}`}>{n}</span>
      {label}
    </span>
  );
};

export const TrialCountdownBanner = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/account/value-summary`);
        if (!cancelled) setData(d);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.trial) return null;
  const { trial } = data;
  if (!trial.is_trial) return null;                  // already paid
  const days = trial.days_left;
  const expired = !!trial.expired;
  const graceMode = !!trial.grace_mode;
  const graceDaysLeft = trial.grace_days_left ?? 0;

  // Stage 12 — Show banner whenever:
  //   • trial has ≤7 days left (day 23+)
  //   • OR trial is expired (grace mode OR fully locked)
  if (!expired && (days === null || days >= 8)) return null;

  const copy = graceMode
    ? graceCopy(graceDaysLeft)
    : (expired
        ? { eyebrow: 'Trial ended', tone: 'navy', headline: 'Your trial just wrapped.', sub: "Your record is safe. Continue when you're ready." }
        : copyFor(days));
  if (!copy) return null;

  const tone = TONE_STYLES[copy.tone] || TONE_STYLES.teal;
  const invert = !!tone.invert;

  return (
    <AnimatePresence>
      <motion.div
        key="trial-countdown"
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`relative rounded-2xl border ${tone.border} ${tone.bg} p-4 sm:p-5 overflow-hidden`}
        data-testid="trial-countdown-banner"
        data-trial-state={graceMode ? `grace-${graceDaysLeft}d` : (expired ? 'expired' : `days-${days}`)}
      >
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${tone.iconBg} ${tone.iconText} flex items-center justify-center`}>
            {expired ? <ShieldCheck size={16} weight="fill" /> : <Clock size={16} weight="fill" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] mb-1 ${tone.eyebrow}`}>
              {copy.eyebrow}
            </p>
            <p
              className={`font-serif text-[18px] sm:text-[20px] leading-snug ${invert ? 'text-white' : 'text-[#0E1C2E]'}`}
              data-testid="trial-countdown-headline"
            >
              {copy.headline}
            </p>
            <p className={`text-[12.5px] mt-1 leading-relaxed ${invert ? 'text-white/70' : 'text-slate-500'}`}>
              {copy.sub}
            </p>

            {/* Accumulated value strip */}
            <div
              className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px]"
              data-testid="trial-countdown-assets"
            >
              <ValueBullet n={data.documents}        label="reports"         invert={invert} />
              <ValueBullet n={data.chats}            label="conversations"   invert={invert} />
              <ValueBullet n={data.one_pagers}       label="one-pagers"      invert={invert} />
              <ValueBullet n={data.doctor_summaries} label="doctor summaries" invert={invert} />
              <ValueBullet n={data.days_tracking}    label="days tracked"    invert={invert} />
            </div>

            {/* CTAs */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/pricing?utm_source=app&utm_medium=banner&utm_campaign=trial_conversion&utm_content=' + (graceMode ? `grace_${graceDaysLeft}` : (expired ? 'expired' : `day_${days}`)))}
                className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors ${
                  invert
                    ? 'bg-white text-[#0E1C2E] hover:bg-slate-50'
                    : 'bg-[#0E1C2E] text-white hover:bg-[#1A2B42]'
                }`}
                data-testid="trial-countdown-upgrade"
              >
                <Sparkle size={12} weight="fill" />
                {graceMode ? 'Continue Kindra' : (expired ? 'Continue Kindra' : 'Keep my Kindra')}
                <ArrowRight size={12} weight="bold" />
              </button>
              {!expired && !graceMode && (
                <p className={`text-[11px] ${invert ? 'text-white/60' : 'text-slate-400'}`}>
                  Nothing locks until you choose.
                </p>
              )}
              {graceMode && (
                <p className={`text-[11px] ${invert ? 'text-white/60' : 'text-slate-400'}`}>
                  Read-only access until grace ends.
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TrialCountdownBanner;
